import styled from '@emotion/styled';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import ServerError from '../../../../../components/ServerError';
import { useToasts } from '../../../../../context/Toast';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import { SkeletonTable } from '../../../components/Skeleton/Table';
import { useTableContentFiltration } from '../../../manager/components/useTableContent';
import {
  useDeleteSkillMutation,
  useManagerSkillsQuery,
  useUpdateSkillMutation,
} from '../../../__generated__/graphql';
import { DeleteSkillDialog } from '../dialog/DeleteSkillDialog';
import { UpdateSkillDialog } from '../dialog/UpdateSkillDialog';
import { mapManagerSkill } from './mappings';
import { ManageSkillsFilter } from './ManageSkillsFilter';
import { ManageSkillsTable } from './ManageSkillTable';
import { ManagerSkill } from './types';
import { IdName } from '../common/types';
import { SkillCategoryFilter } from './SkillCategoryFilter';

const FilterWrapper = styled.div({
  paddingLeft: '1rem',
  paddingRight: '1rem',
});

const CategoryFilterContainer = styled.div({
  minWidth: '12rem',
});

export const ManageSkillsConnected: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { t } = useTranslation();
  const [filter, onFilterChange] = useTableContentFiltration();
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [skillToUpdate, setSkillToUpdate] = useState<
    (IdName & { categoryId?: string | null }) | null
  >(null);
  const [skillToDelete, setSkillToDelete] = useState<IdName | null>(null);

  const { data, loading, error } = useManagerSkillsQuery();

  const skills: ManagerSkill[] | undefined = useMemo(
    () => data?.skills.map(mapManagerSkill),
    [data],
  );

  // Precomputed once per skill list, not on every keystroke: match the
  // canonical name and any translation so a German/French/etc. name finds the
  // skill even though the table shows the base name.
  const searchTermsById = useMemo(
    () =>
      new Map(
        skills?.map((s) => [
          s.id,
          [s.name, ...Object.values(s.translations)]
            .filter(Boolean)
            .join(' ')
            .toLowerCase(),
        ]) ?? [],
      ),
    [skills],
  );

  const filteredSkills = useMemo(() => {
    const byCategory = categoryFilter
      ? skills?.filter((s) => s.category?.id === categoryFilter)
      : skills;

    const term = filter.trim().toLowerCase();
    if (!term) {
      return byCategory;
    }
    return byCategory?.filter((s) =>
      (searchTermsById.get(s.id) ?? '').includes(term),
    );
  }, [skills, categoryFilter, filter, searchTermsById]);

  const [updateSkill] = useUpdateSkillMutation();
  const [deleteSkill] = useDeleteSkillMutation();
  const errorHandler = useMutationErrorHandler();
  const { addSuccess, addError } = useToasts();

  const handleDeleteSkill = useCallback(async () => {
    if (skillToDelete === null) {
      return;
    }
    await deleteSkill({
      variables: {
        input: {
          id: skillToDelete.id,
        },
      },
    })
      .then((r) => {
        if (r.data?.skills.deleteSkill.error) {
          addError(r.data?.skills.deleteSkill.error);
          return;
        }
        addSuccess(t('skills.deleteSkill.success'));
      })
      .catch(errorHandler)
      .finally(() => {
        setSkillToDelete(null);
      });
  }, [
    deleteSkill,
    errorHandler,
    addSuccess,
    addError,
    t,
    setSkillToDelete,
    skillToDelete,
  ]);
  const handleUpdateSkill = useCallback(
    async (name: string, categoryId: string | null) => {
      if (skillToUpdate === null) {
        return;
      }
      await updateSkill({
        variables: {
          input: {
            id: skillToUpdate.id,
            name: name,
            categoryId: categoryId,
          },
        },
      })
        .then((r) => {
          if (r.data?.skills.updateSkill.error) {
            addError(r.data?.skills.updateSkill.error);
            return;
          }
          addSuccess(t('skills.updateSkill.success'));
        })
        .catch(errorHandler)
        .finally(() => {
          setSkillToUpdate(null);
        });
    },
    [
      updateSkill,
      errorHandler,
      addSuccess,
      addError,
      t,
      setSkillToUpdate,
      skillToUpdate,
    ],
  );

  const handleUpdateClick = useCallback(
    (skillId: string) => {
      const skill = skills?.find((x) => x.id === skillId);
      if (skill) {
        setSkillToUpdate({
          id: skill.id,
          name: skill.name,
          categoryId: skill.category?.id ?? null,
        });
      }
    },
    [setSkillToUpdate, skills],
  );

  const handleDeleteClick = useCallback(
    (skillId: string) => {
      const skill = skills?.find((x) => x.id === skillId);
      if (skill) {
        setSkillToDelete(skill);
      }
    },
    [setSkillToDelete, skills],
  );

  const handleSkillUpdateClose = useCallback(() => {
    setSkillToUpdate(null);
  }, [setSkillToUpdate]);

  const handleSkillDeleteClose = useCallback(() => {
    setSkillToDelete(null);
  }, [setSkillToDelete]);

  if (error) {
    return <ServerError />;
  }

  return (
    <>
      <FilterWrapper className="d-flex gap-3 align-items-center">
        <div className="flex-grow-1">
          <ManageSkillsFilter
            onFilterChange={onFilterChange}
            placeholder={t('skills.manager.filter.placeholder')}
          />
        </div>
        <CategoryFilterContainer>
          <SkillCategoryFilter
            value={categoryFilter}
            onChange={setCategoryFilter}
          />
        </CategoryFilterContainer>
      </FilterWrapper>
      {!loading && filteredSkills ? (
        <ManageSkillsTable
          onDeleteClick={handleDeleteClick}
          onUpdateClick={handleUpdateClick}
          filter=""
          data={filteredSkills}
        />
      ) : (
        <Loading />
      )}
      {skillToUpdate !== null && (
        <UpdateSkillDialog
          show={true}
          skill={skillToUpdate}
          onClose={handleSkillUpdateClose}
          onSubmit={handleUpdateSkill}
          allSkills={skills ?? []}
        />
      )}
      <DeleteSkillDialog
        show={skillToDelete !== null}
        skillName={skillToDelete?.name ?? ''}
        handleClose={handleSkillDeleteClose}
        onSubmit={handleDeleteSkill}
      />
    </>
  );
};

const Loading = () => {
  return (
    <>
      <Skeleton height={'3rem'} />
      <SkeletonTable columns={5} rows={5} />
    </>
  );
};
